import * as React from 'react';
import { styled } from '@linaria/react';

import { clamp, sampleOne } from '@/utils';
import useSound from '@/hooks/use-sound';

import ColorModeContainer from '@/components/ColorModeContainer';
import SvgWithGrid from '@/components/SvgWithGrid';
import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';

import Timeline from './Timeline';
import Cursor from './Cursor';
import { getCurrentCommandIndex } from './SelfDrawingPath.helpers';
import DrawnPathParts from './DrawnPathParts';
import SelfDrawingPseudoCodeSnippet from './SelfDrawingPseudoCodeSnippet';
import MorphingPlayStopIcon from './MorphingPlayStopIcon';
import type { Command, InputCommand } from './SelfDrawingPath.types';

const BASE_TIME_PER_COMMAND = 1000;

const VIEWBOX_SIZE = 16;
const RENDERED_SIZE = 320;
const MULTIPLIER = RENDERED_SIZE / VIEWBOX_SIZE;

interface Point {
  x: number;
  y: number;
}

interface Props {
  startFrom?: Point;
  commands: Array<InputCommand>;
}

function SelfDrawingPath({
  startFrom,
  commands: inputCommands,
}: Props) {
  const [isCoordinateVisible, setIsCoordinateVisible] =
    React.useState(false);
  const [coordinatePosition, setCoordinatePosition] = React.useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });

  const svgRef = React.useRef<SVGSVGElement>(null);

  const [playPenSound, { stop: stopPenSound }] = useSound(
    '/sounds/pen-scraping.mp3',
    {
      volume: 0.5,
      sprite: {
        runA: [500, 1500],
        runB: [3000, 1700],
        endA: [5000, 100],
      },
    }
  );

  // The commands we pass in have a `durationScale` property, a relative value from 0 to 1. We want to turn this into a raw # of milliseconds right away, so that we don’t have to worry about this later on:
  const commands: Array<Command> = React.useMemo(() => {
    return inputCommands.map((command) => ({
      ...command,
      duration: command.durationScale * BASE_TIME_PER_COMMAND,
    }));
  }, [inputCommands]);

  const totalTime = commands.reduce(
    (acc, command) => acc + command.duration,
    0
  );

  const [time, setTime] = React.useState(0);
  const [status, setStatus] = React.useState<'idle' | 'playing'>(
    'idle'
  );

  const currentCommandIndex = getCurrentCommandIndex(time, commands);
  const isAtBeginningOrEnd = time === 0 || time === totalTime;

  React.useEffect(() => {
    if (!isCoordinateVisible) {
      return;
    }

    function handlePointerMove(event: PointerEvent) {
      if (!svgRef.current) {
        return;
      }

      const rect = svgRef.current.getBoundingClientRect();
      let x = (event.clientX - rect.left) / MULTIPLIER;
      let y = (event.clientY - rect.top) / MULTIPLIER;

      x = Math.round(x);
      y = Math.round(y);

      if (x < -2 || x > 18 || y < -2 || y > 18) {
        setIsCoordinateVisible(false);
        return;
      }

      setCoordinatePosition({
        x: clamp(x, 0, 16),
        y: clamp(y, 0, 16),
      });
    }

    window.addEventListener('pointermove', handlePointerMove);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, [isCoordinateVisible]);

  React.useEffect(() => {
    if (status !== 'playing') {
      return;
    }

    const startTime = performance.now();
    let animationFrameId: number;

    function tick() {
      const currentTime = performance.now();
      const timeElapsed = currentTime - startTime;

      if (timeElapsed > totalTime) {
        setTime(totalTime);
        setStatus('idle');
      } else {
        setTime(timeElapsed);
        animationFrameId = window.requestAnimationFrame(tick);
      }
    }

    window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [status, totalTime]);

  React.useEffect(() => {
    if (status === 'playing') {
      const command = commands[currentCommandIndex];

      if (command.type === 'line') {
        const spriteId = sampleOne(['runA', 'runB']);
        playPenSound({ id: spriteId });
      } else {
        stopPenSound();
      }
    } else {
      stopPenSound();
    }
  }, [
    status,
    currentCommandIndex,
    commands,
    playPenSound,
    stopPenSound,
  ]);

  const handlePointerEnter = React.useCallback(() => {
    setIsCoordinateVisible(true);
  }, []);

  const handleScrub = React.useCallback((time: number) => {
    setTime(time);
    setStatus('idle');
  }, []);

  const handleNudgeCoordinate = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      let newX = coordinatePosition.x;
      let newY = coordinatePosition.y;

      if (event.key === 'ArrowUp') {
        newY -= 1;
      } else if (event.key === 'ArrowDown') {
        newY += 1;
      } else if (event.key === 'ArrowLeft') {
        newX -= 1;
      } else if (event.key === 'ArrowRight') {
        newX += 1;
      } else {
        return;
        // Ignore other keypresses
      }

      // Stop the arrow keys from moving the viewport
      event.preventDefault();

      setCoordinatePosition({
        x: clamp(newX, 0, 16),
        y: clamp(newY, 0, 16),
      });
    },
    [coordinatePosition]
  );

  const handleTogglePlay = React.useCallback(() => {
    if (status === 'playing') {
      setStatus('idle');
      return;
    }

    setTime(0);
    setStatus('playing');
  }, [status]);

  const isMouseVisible = isCoordinateVisible && !!coordinatePosition;

  return (
    <Wrapper colorMode="dark">
      <Row>
        <CodeWrapper>
          <SelfDrawingPseudoCodeSnippet
            commands={commands}
            highlightedCommandIndex={
              isAtBeginningOrEnd ? null : currentCommandIndex
            }
          />
        </CodeWrapper>
        <SvgWrapper>
          <Svg
            ref={svgRef}
            gridGap={1}
            lineWidth={0.1}
            viewBox="0 0 16 16"
            onPointerEnter={handlePointerEnter}
          >
            <DrawnPathParts
              time={time}
              currentCommandIndex={currentCommandIndex}
              commands={commands}
            />
            <Cursor
              time={time}
              isVisible={!isAtBeginningOrEnd}
              currentCommandIndex={currentCommandIndex}
              commands={commands}
              startFrom={startFrom}
            />
          </Svg>
          <CoordinateLabel
            data-is-visible={isMouseVisible}
            style={{
              top: coordinatePosition.y * MULTIPLIER,
              left: coordinatePosition.x * MULTIPLIER,
            }}
          >
            ({coordinatePosition.x}, {coordinatePosition.y})
          </CoordinateLabel>
          <CoordinateDot
            data-is-visible={isMouseVisible}
            onFocus={handlePointerEnter}
            onBlur={() => {
              setIsCoordinateVisible(false);
            }}
            onKeyDown={handleNudgeCoordinate}
            style={{
              top: coordinatePosition.y * MULTIPLIER,
              left: coordinatePosition.x * MULTIPLIER,
            }}
          />
        </SvgWrapper>
      </Row>
      <ActionsWrapper>
        <Timeline
          time={time}
          totalTime={totalTime}
          commands={commands}
          highlightedCommandIndex={
            isAtBeginningOrEnd ? null : currentCommandIndex
          }
          status={status}
          handleScrub={handleScrub}
          handleTogglePlay={handleTogglePlay}
        />
        <PlayButton
          Icon={MorphingPlayStopIcon}
          iconProps={{ status }}
          onClick={handleTogglePlay}
        >
          {status === 'playing' ? 'Stop' : 'Play'}
        </PlayButton>
      </ActionsWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(ColorModeContainer)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-inline: -16px;
  margin-bottom: 32px;
  padding: 16px;
  border: var(--page-border);
  border-radius: 8px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;

const CodeWrapper = styled.div`
  flex: 1;
  width: 292px;
  min-width: 280px;
  max-width: 100%;
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
  /* On VERY small screens (unrealistically small?), the text overflows. If I set “overflow: auto”, it breaks the flex-wrap on the parent. Since I'm not too worried about this, we'll hide the overflow: */
  overflow: clip;
`;

const SvgWrapper = styled.div`
  position: relative;
`;

const Svg = styled(SvgWithGrid)`
  width: ${RENDERED_SIZE}px;
  height: ${RENDERED_SIZE}px;
  outline: 1px solid var(--color-gray-300);
  outline-offset: -1px;
  border-radius: 4px;
  cursor: none;
  contain: layout;
`;

const ActionsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 16px;
  border: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const PlayButton = styled(ButtonPrimary)`
  align-self: center;
  width: 250px;
`;

const CoordinateLabel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  white-space: nowrap;
  transform: translate(-50%, -100%);
  background: var(--color-background);
  user-select: none;
  font-size: 0.75rem;
  font-family: var(--font-family-mono);
  font-weight: 500;
  color: var(--color-text);
  padding: 2px 4px;
  border-radius: 4px;
  pointer-events: none;
  margin-top: -8px;
  opacity: 0;
  transition: opacity 300ms 500ms ease-in-out;

  &[data-is-visible='true'] {
    opacity: 1;
    transition: opacity 200ms ease-in-out;
  }
`;

const CoordinateDot = styled.button`
  --size: 13px;
  position: absolute;
  width: var(--size);
  height: var(--size);
  background: var(--color-text);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid var(--color-background);
  opacity: 0;
  transition: opacity 300ms 500ms ease-in-out;
  pointer-events: none;

  &[data-is-visible='true'] {
    opacity: 1;
    transition: opacity 200ms ease-in-out;
  }
`;

export default SelfDrawingPath;
